import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, ArrowDown, ArrowUp, ArrowUpDown, Download, Loader2, Search } from 'lucide-react';
import {
  apiService,
  AllCustomersCostOverview,
  CostOverviewSortField,
  Customer,
  CustomerCostOverview,
} from '../../services/api';
import { toast } from 'sonner';
import { CustomerCostOverviewDialog } from '../customer/CustomerCostOverviewDialog';

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const today = () => isoDay(new Date());
const daysAgo = (n: number) => isoDay(new Date(Date.now() - n * 24 * 60 * 60 * 1000));
/** Von-Datum für "N Tage": letzte N Kalendertage inklusive heute (N=1 → heute). */
const lastNDaysFrom = (days: number) => daysAgo(days - 1);

/** Quick-Select-Zeiträume in Tagen (1–7 einzeln, plus 30/90). */
const QUICK_RANGE_DAYS = [1, 2, 3, 4, 5, 6, 7, 30, 90];

const eur = (n: number | null | undefined): string => {
  if (n == null) return '–';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
};
const min1 = (n: number) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

const PAGE_SIZE = 50;

export function CostOverviewPage() {
  // date inputs (only applied on "Anwenden" / quick-select)
  const [from, setFrom] = useState<string>(lastNDaysFrom(30));
  const [to, setTo] = useState<string>(today());
  // the range actually fetched
  const [range, setRange] = useState<{ from: string; to: string }>({ from: lastNDaysFrom(30), to: today() });

  const [sortBy, setSortBy] = useState<CostOverviewSortField>('totalCostEur');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('DESC');
  const [page, setPage] = useState(1);
  // search input is debounced into `search`, which drives the server-side filter
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AllCustomersCostOverview | null>(null);
  const [drillCustomer, setDrillCustomer] = useState<Customer | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getAllCustomersCostOverview({
        from: `${range.from}T00:00:00Z`,
        to: `${range.to}T23:59:59Z`,
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        sortBy,
        sortDirection,
      });
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Fehler beim Laden der Kostenübersicht');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, page, search, sortBy, sortDirection]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const applyRange = (f: string, t: string) => {
    // same-day ranges are valid: the fetch spans T00:00:00Z–T23:59:59Z
    if (!f || !t || f > t) {
      toast.error('Ungültiger Zeitraum: "Von" darf nicht nach "Bis" liegen.');
      return;
    }
    setPage(1);
    setRange({ from: f, to: t });
  };

  const handleApply = () => applyRange(from, to);

  const handleQuickRange = (days: number) => {
    const f = lastNDaysFrom(days);
    const t = today();
    setFrom(f);
    setTo(t);
    setPage(1);
    setRange({ from: f, to: t });
  };

  const handleSort = (field: CostOverviewSortField) => {
    if (sortBy === field) {
      setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortDirection('ASC');
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: CostOverviewSortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline-block text-gray-400" />;
    if (sortDirection === 'ASC') return <ArrowUp className="w-3 h-3 ml-1 inline-block text-blue-600" />;
    return <ArrowDown className="w-3 h-3 ml-1 inline-block text-blue-600" />;
  };

  const Th = ({ field, label, align = 'right' }: { field: CostOverviewSortField; label: string; align?: 'left' | 'right' }) => (
    <TableHead
      className={`cursor-pointer select-none ${align === 'right' ? 'text-right' : ''}`}
      onClick={() => handleSort(field)}
    >
      {label}
      <SortIcon field={field} />
    </TableHead>
  );

  const handleExportCsv = () => {
    if (!data) return;
    const header = ['Kunde', 'Abo-Plan', 'LLM (EUR)', 'LLM Input (EUR)', 'LLM Cached Input (EUR)', 'LLM Output (EUR)', 'Background LLM Jobs (EUR)', 'Speech-to-Text (EUR)', 'Text-to-Speech (EUR)', 'RAG (EUR)', 'KI-Assistent (EUR)', 'KI-Assistent Calls', 'QA (EUR)', 'QA Eval-Service (EUR)', 'QA Bot-LLM (EUR)', 'Gesamtkosten (EUR)', 'Umsatz (EUR)', 'Marge (EUR)', 'Telefonminuten', 'Sessions'];
    const rows = data.items.map((it) => [
      it.customerName,
      it.subscriptionPlanName ?? '',
      it.llm.costEur.toFixed(4),
      it.llm.inputCostEur.toFixed(4),
      (it.llm.cachedInputCostEur ?? 0).toFixed(4),
      it.llm.outputCostEur.toFixed(4),
      it.llm.hookEvaluation.costEur.toFixed(4),
      it.stt.costEur.toFixed(4),
      it.tts.costEur.toFixed(4),
      it.rag.costEur.toFixed(4),
      it.internalTools.costEur.toFixed(4),
      String(it.internalTools.calls),
      it.qa.costEur.toFixed(4),
      it.qa.evalServiceCostEur.toFixed(4),
      it.qa.botLlmCostEur.toFixed(4),
      it.totalCostEur.toFixed(4),
      it.estimatedRevenueEur != null ? it.estimatedRevenueEur.toFixed(4) : '',
      it.estimatedMarginEur != null ? it.estimatedMarginEur.toFixed(4) : '',
      it.callMinutes.toFixed(1),
      String(it.sessionCount),
    ]);
    const totalsRow = [
      'GESAMT',
      '',
      data.totals.llmEur.toFixed(4),
      '',
      '',
      '',
      data.totals.backgroundLlmEur.toFixed(4),
      data.totals.sttEur.toFixed(4),
      data.totals.ttsEur.toFixed(4),
      data.totals.ragEur.toFixed(4),
      data.totals.internalToolsEur.toFixed(4),
      '',
      data.totals.qaEur.toFixed(4),
      '',
      '',
      data.totals.totalCostEur.toFixed(4),
      data.totals.estimatedRevenueEur.toFixed(4),
      data.totals.estimatedMarginEur.toFixed(4),
      data.totals.callMinutes.toFixed(1),
      String(data.totals.sessionCount),
    ];
    const csv = [header, ...rows, totalsRow]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kostenuebersicht-${range.from}-${range.to}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const openDrill = (it: CustomerCostOverview) => {
    setDrillCustomer({ id: it.customerId, name: it.customerName } as Customer);
  };

  const marginClass = (n: number | null | undefined) => (n != null && n < 0 ? 'text-red-600 font-medium' : 'text-green-700 font-medium');
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kostenübersicht</h1>
          <p className="text-gray-600">Geschätzte Plattform-Kosten (LLM, STT, TTS, RAG, QA), Umsatz und Marge je Kunde</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Zeitraum</CardTitle>
          <CardDescription>Werte sind Schätzungen basierend auf den pro Modell konfigurierten Preisen.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="co-from">Von</Label>
              <Input id="co-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="co-to">Bis</Label>
              <Input id="co-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <Button onClick={handleApply} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Anwenden
            </Button>
            <div className="flex gap-2 flex-wrap">
              {QUICK_RANGE_DAYS.map((d) => (
                <Button key={d} variant="outline" size="sm" onClick={() => handleQuickRange(d)} disabled={loading}>
                  {d === 1 ? '1 Tag' : `${d} Tage`}
                </Button>
              ))}
            </div>
            <div className="space-y-1 ml-auto">
              <Label htmlFor="co-search">Kunde suchen</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  id="co-search"
                  className="pl-8 w-56"
                  placeholder="Kundenname…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={!data || loading}>
                <Download className="w-4 h-4 mr-2" />
                CSV Export
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading && !data ? (
            <div className="flex items-center justify-center p-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Lade Kostenübersicht...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <Th field="customerName" label="Kunde" align="left" />
                  <Th field="subscriptionPlanName" label="Abo-Plan" align="left" />
                  <Th field="llm" label="LLM" />
                  <Th field="backgroundLlm" label="Background LLM Jobs" />
                  <Th field="stt" label="STT" />
                  <Th field="tts" label="TTS" />
                  <Th field="rag" label="RAG" />
                  <Th field="internalTools" label="KI-Assistent" />
                  <Th field="qa" label="QA" />
                  <Th field="totalCostEur" label="Gesamtkosten" />
                  <Th field="estimatedRevenueEur" label="Umsatz" />
                  <Th field="estimatedMarginEur" label="Marge" />
                  <Th field="callMinutes" label="Telefonminuten" />
                  <Th field="sessionCount" label="Sessions" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.map((it) => (
                  <TableRow key={it.customerId} className="cursor-pointer hover:bg-gray-50" onClick={() => openDrill(it)}>
                    <TableCell className="font-medium">
                      {it.customerName}
                      {(it.llm.unpriced || it.stt.unpriced || it.tts.unpriced || it.qa.unpriced) && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 ml-2 text-[10px]">Pricing unvollständig</Badge>
                      )}
                    </TableCell>
                    <TableCell>{it.subscriptionPlanName ?? <span className="text-gray-400">—</span>}</TableCell>
                    <TableCell className="text-right">{eur(it.llm.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.llm.hookEvaluation.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.stt.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.tts.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.rag.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.internalTools.costEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.qa.costEur)}</TableCell>
                    <TableCell className="text-right font-medium">{eur(it.totalCostEur)}</TableCell>
                    <TableCell className="text-right">{eur(it.estimatedRevenueEur)}</TableCell>
                    <TableCell className={`text-right ${marginClass(it.estimatedMarginEur)}`}>{eur(it.estimatedMarginEur)}</TableCell>
                    <TableCell className="text-right">{min1(it.callMinutes)}</TableCell>
                    <TableCell className="text-right">{it.sessionCount}</TableCell>
                  </TableRow>
                ))}
                {data && data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center text-gray-500 py-8">Keine Kunden</TableCell>
                  </TableRow>
                )}
                {data && (
                  <TableRow className="border-t-2 bg-gray-50 font-semibold">
                    <TableCell>GESAMT</TableCell>
                    <TableCell />
                    <TableCell className="text-right">{eur(data.totals.llmEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.backgroundLlmEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.sttEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.ttsEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.ragEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.internalToolsEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.qaEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.totalCostEur)}</TableCell>
                    <TableCell className="text-right">{eur(data.totals.estimatedRevenueEur)}</TableCell>
                    <TableCell className={`text-right ${marginClass(data.totals.estimatedMarginEur)}`}>{eur(data.totals.estimatedMarginEur)}</TableCell>
                    <TableCell className="text-right">{min1(data.totals.callMinutes)}</TableCell>
                    <TableCell className="text-right">{data.totals.sessionCount}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <p>Seite {data.page} von {totalPages} ({data.total} Kunden)</p>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1 || loading}>Zurück</Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages || loading}>Weiter</Button>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Klick auf eine Zeile öffnet die Detail-Aufschlüsselung des Kunden. Die gleiche Übersicht ist auch unter „Kunden" → Kunde → „Kostenübersicht" erreichbar.
        „Background LLM Jobs" (aktuell: Hookevaluierung nach Session-Ende) ist eine informative Teilmenge der LLM-Spalte und zählt nicht zusätzlich in die Gesamtkosten.
      </p>

      <CustomerCostOverviewDialog
        customer={drillCustomer}
        initialFrom={range.from}
        initialTo={range.to}
        onClose={() => setDrillCustomer(null)}
      />
    </div>
  );
}
